import { useEffect, useState } from "react";
import { Navigate, Link, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, Plus, Pencil, Trash2, ExternalLink, Bell, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";
import SEO from "../../components/SEO";
import { fmtDate } from "../../lib/format";
import AdminCalendrier from "./AdminCalendrier";
import AdminMembres from "./AdminMembres";
import AdminLicencies from "./AdminLicencies";
import AdminAdherents from "./AdminAdherents";
import AdminResultats from "./AdminResultats";
import AdminProjet from "./AdminProjet";
import AdminTarifs from "./AdminTarifs";
import AdminPaiements from "./AdminPaiements";

const EMPTY_RESULTAT = { nom: "", date: "", categorie: "", coureurs: [], a_venir: false };
const EMPTY = { title: "", category: "Club", author: "Club Cycliste Le Boulou", date: "", image: "", video_url: "", external_url: "", external_label: "", content: "", status: "draft", show_in_results: false, resultat_course: { ...EMPTY_RESULTAT } };
const STATUTS = ["EN ATTENTE", "PAYÉ", "CONTACTÉ", "CONFIRMÉ"];
const ADH_STATUTS = ["INSCRIPTION EN COURS", "CATÉGORIE À VALIDER", "ÉCOLE DE VÉLO VALIDÉE", "LICENCIÉ CLUB VALIDÉ", "PAIEMENT EN ATTENTE", "INSCRIPTION VALIDÉE", "DOSSIER À VÉRIFIER", "ADHÉSION EXPIRÉE"];
const ADH_FILTRES = [
  { v: "tous", label: "Tous les adhérents" },
  { v: "ffc", label: "FFC" },
  { v: "fsgt", label: "FSGT" },
  { v: "ffc_fsgt", label: "FFC + FSGT" },
  { v: "aucune", label: "Sans licence" },
  { v: "en_attente", label: "Paiement en attente" },
  { v: "validee", label: "Inscription validée" },
  { v: "a_verifier", label: "Dossier à vérifier" },
];

const ADH_STATUT_STYLES = {
  "INSCRIPTION EN COURS": "bg-steel/15 text-steel",
  "CATÉGORIE À VALIDER": "bg-amber-100 text-amber-700",
  "ÉCOLE DE VÉLO VALIDÉE": "bg-blue-100 text-blue-700",
  "LICENCIÉ CLUB VALIDÉ": "bg-blue-100 text-blue-700",
  "PAIEMENT EN ATTENTE": "bg-steel/15 text-steel",
  "INSCRIPTION VALIDÉE": "bg-green-100 text-green-700",
  "DOSSIER À VÉRIFIER": "bg-amber-100 text-amber-700",
  "ADHÉSION EXPIRÉE": "bg-rouge/10 text-rouge",
};

const STATUT_STYLES = {
  "EN ATTENTE": "bg-steel/15 text-steel",
  "PAYÉ": "bg-green-100 text-green-700",
  "CONTACTÉ": "bg-blue-100 text-blue-700",
  "CONFIRMÉ": "bg-rouge/10 text-rouge",
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "actus");
  const [notifs, setNotifs] = useState({ items: [], non_lues: 0 });
  const [notifsOpen, setNotifsOpen] = useState(false);

  const loadNotifs = async () => {
    try {
      const { data } = await api.get("/admin/notifications");
      setNotifs(data);
    } catch { /* non bloquant */ }
  };

  const openNotif = async (n) => {
    try {
      await api.post(`/admin/notifications/${n.id}/lue`);
    } catch { /* non bloquant */ }
    window.location.href = `/admin?tab=membres&inscription=${n.adhesion_id}`;
  };

  const toutMarquerLu = async () => {
    try {
      await api.post("/admin/notifications/toutes-lues");
      loadNotifs();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(true);
  const [requests, setRequests] = useState([]);
  const [busyPel, setBusyPel] = useState(false);
  const [adhesions, setAdhesions] = useState([]);
  const [busyAdh, setBusyAdh] = useState(false);
  const [filtreAdh, setFiltreAdh] = useState("tous");

  const loadAdhesions = async (filtre = filtreAdh) => {
    setBusyAdh(true);
    try {
      const { data } = await api.get(`/admin/adhesions?filtre=${filtre}`);
      setAdhesions(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyAdh(false);
    }
  };

  const exportAdhesions = async () => {
    try {
      const { data } = await api.get("/admin/adhesions/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "adhesions-club-cycliste-le-boulou.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const changeAdhStatut = async (adh, statut) => {
    try {
      await api.patch(`/admin/adhesions/${adh.id}/statut`, { statut });
      setAdhesions((prev) => prev.map((r) => (r.id === adh.id ? { ...r, statut } : r)));
      toast.success(`Statut mis à jour : ${statut}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/admin/articles");
      setArticles(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const loadPeloton = async () => {
    setBusyPel(true);
    try {
      const { data } = await api.get("/admin/peloton");
      setRequests(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyPel(false);
    }
  };

  useEffect(() => {
    api.get("/meta/categories").then((r) => setCategories(r.data.article_categories)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      load();
      loadPeloton();
      loadAdhesions();
      loadNotifs();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center">
        <Loader2 className="animate-spin text-rouge" size={32} />
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  const startNew = () => {
    setEditing("new");
    setForm(EMPTY);
  };

  const startEdit = (a) => {
    setEditing(a.id);
    setForm({
      title: a.title, category: a.category, author: a.author, date: a.date,
      image: a.image || "", video_url: a.video_url || "", external_url: a.external_url || "", external_label: a.external_label || "", content: a.content || "", status: a.status,
      show_in_results: a.show_in_results || false,
      resultat_course: { ...EMPTY_RESULTAT, ...(a.resultat_course || {}) },
    });
    window.scrollTo({ top: 0 });
  };

  const setCourse = (patch) => setForm((f) => ({ ...f, resultat_course: { ...f.resultat_course, ...patch } }));
  const setRider = (i, patch) => setCourse({ coureurs: form.resultat_course.coureurs.map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const addRider = () => setCourse({ coureurs: [...form.resultat_course.coureurs, { nom: "", place: "" }] });
  const removeRider = (i) => setCourse({ coureurs: form.resultat_course.coureurs.filter((_, idx) => idx !== i) });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, date: form.date || undefined };
      if (editing === "new") {
        await api.post("/admin/articles", payload);
        toast.success("Article créé");
      } else {
        await api.put(`/admin/articles/${editing}`, payload);
        toast.success("Article mis à jour");
      }
      setEditing(null);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Supprimer « ${a.title} » ?`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("Article supprimé");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const changeStatut = async (req, statut) => {
    try {
      await api.patch(`/admin/peloton/${req.id}/statut`, { statut });
      setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, statut } : r)));
      toast.success(`Statut mis à jour : ${statut}`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const inputCls = "w-full border border-navy/20 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:border-rouge transition-colors duration-300";
  const labelCls = "block font-heading uppercase tracking-wider text-xs text-navy/70 mb-1.5";

  return (
    <div className="min-h-screen bg-mist">
      <SEO title="Administration" />
      <header className="bg-navy text-white">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="font-heading uppercase tracking-[0.3em] text-rouge text-xs">Administration</p>
            <h1 className="font-heading uppercase font-semibold tracking-tight text-2xl">Club Cycliste Le Boulou</h1>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <button
                onClick={() => { setNotifsOpen(!notifsOpen); if (!notifsOpen) loadNotifs(); }}
                data-testid="admin-notifs-bell"
                className="relative inline-flex items-center justify-center border border-white/25 text-white/80 p-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
                aria-label="Notifications"
              >
                <Bell size={16} />
                {notifs.non_lues > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-rouge text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full" data-testid="admin-notifs-count">
                    {notifs.non_lues}
                  </span>
                )}
              </button>
              {notifsOpen && (
                <div className="absolute right-0 top-12 z-50 w-[26rem] max-w-[90vw] bg-white text-navy shadow-2xl border border-navy/10" data-testid="admin-notifs-panel">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-navy/10">
                    <p className="font-heading uppercase tracking-wider text-xs text-navy/70">Notifications ({notifs.non_lues} non lue{notifs.non_lues > 1 ? "s" : ""})</p>
                    {notifs.non_lues > 0 && (
                      <button onClick={toutMarquerLu} data-testid="admin-notifs-read-all" className="inline-flex items-center gap-1.5 text-xs text-steel hover:text-rouge transition-colors">
                        <CheckCheck size={13} /> Tout marquer lu
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifs.items.length === 0 ? (
                      <p className="px-5 py-6 text-sm text-steel" data-testid="admin-notifs-empty">Aucune notification.</p>
                    ) : (
                      notifs.items.map((n) => (
                        <button
                          key={n.id}
                          onClick={() => openNotif(n)}
                          data-testid={`notif-${n.id.slice(0, 8)}`}
                          className={`w-full text-left px-5 py-4 border-b border-navy/5 hover:bg-mist transition-colors ${n.lu ? "opacity-60" : ""}`}
                        >
                          <div className="flex items-center gap-2">
                            {!n.lu && <span className="w-2 h-2 rounded-full bg-rouge shrink-0" />}
                            <p className="font-heading uppercase font-semibold text-sm text-navy">{n.titre}</p>
                          </div>
                          <p className="text-xs text-steel mt-1">
                            {n.prenom} {n.nom}
                            {n.details?.categorie ? ` · ${n.details.categorie}` : ""}
                            {n.details?.licence ? ` · ${n.details.licence}` : ""}
                            {n.details?.pack ? ` · Pack ${n.details.pack}` : ""}
                            {n.details?.montant ? ` · FSGT : ${n.details.fsgt || "Non"}${n.details.fsgt_montant ? ` (${n.details.fsgt_montant})` : ""}` : ""}
                            {n.details?.montant ? ` · Total ${n.details.montant}` : ""}
                            {n.details?.mineur ? " · mineur" : ""}
                            {n.details?.documents_valides ? ` · ${n.details.documents_valides} document(s) validé(s)` : ""}
                          </p>
                          <p className="text-[10px] text-steel/70 mt-1">{fmtDate(n.created_at)} {(n.created_at || "").slice(11, 16)} · Voir le dossier →</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            <Link to="/" className="text-white/60 hover:text-white text-xs uppercase tracking-widest transition-colors" data-testid="admin-back-site">
              Voir le site
            </Link>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 border border-white/25 text-white/80 px-4 py-2.5 text-xs font-heading uppercase tracking-wider hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
            >
              <LogOut size={14} /> Déconnexion
            </button>
          </div>
        </div>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 flex flex-wrap md:flex-nowrap md:overflow-x-auto gap-2 pb-4">
          <button
            onClick={() => setTab("actus")}
            data-testid="admin-tab-actus"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "actus" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Actualités
          </button>
          <button
            onClick={() => setTab("peloton")}
            data-testid="admin-tab-peloton"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "peloton" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Peloton des Entreprises
          </button>
          <button
            onClick={() => setTab("adhesions")}
            data-testid="admin-tab-adhesions"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "adhesions" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Adhésions
          </button>
          <button
            onClick={() => setTab("calendrier")}
            data-testid="admin-tab-calendrier"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "calendrier" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Calendrier
          </button>
          <button
            onClick={() => setTab("membres")}
            data-testid="admin-tab-membres"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "membres" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Inscriptions 2027
          </button>
          <button
            onClick={() => setTab("paiements")}
            data-testid="admin-tab-paiements"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "paiements" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Paiements
          </button>
          <button
            onClick={() => setTab("tarifs")}
            data-testid="admin-tab-tarifs"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "tarifs" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Tarifs 2027
          </button>
          <button
            onClick={() => setTab("adherents")}
            data-testid="admin-tab-adherents"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "adherents" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Adhérents
          </button>
          <button
            onClick={() => setTab("licencies")}
            data-testid="admin-tab-licencies"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "licencies" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Espace Licencié
          </button>
          <button
            onClick={() => setTab("resultats")}
            data-testid="admin-tab-resultats"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "resultats" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Résultats
          </button>
          <button
            onClick={() => setTab("projet")}
            data-testid="admin-tab-projet"
            className={`font-heading uppercase tracking-wider text-xs px-5 py-2.5 border transition-[background-color,color,border-color] duration-300 ${
              tab === "projet" ? "bg-rouge border-rouge text-white" : "border-white/25 text-white/70 hover:border-rouge hover:text-rouge"
            }`}
          >
            Projet Sportif
          </button>
        </div>
      </header>

      {tab === "calendrier" && <AdminCalendrier />}
      {tab === "membres" && <AdminMembres />}
      {tab === "tarifs" && <AdminTarifs />}
      {tab === "paiements" && <AdminPaiements />}
      {tab === "adherents" && <AdminAdherents />}
      {tab === "licencies" && <AdminLicencies />}
      {tab === "resultats" && <AdminResultats />}
      {tab === "projet" && <AdminProjet />}

      {tab === "actus" && (
        <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
          <div className="flex items-center justify-between mb-8">
            <p className="text-steel text-sm" data-testid="admin-count">{articles.length} article{articles.length > 1 ? "s" : ""}</p>
            <button
              onClick={startNew}
              data-testid="admin-new-article"
              className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-6 py-3 hover:bg-navy transition-colors duration-300"
            >
              <Plus size={16} /> Nouvel article
            </button>
          </div>

          {editing && (
            <form onSubmit={save} data-testid="admin-editor" className="bg-white border border-navy/10 p-6 sm:p-8 mb-10 space-y-5">
              <h2 className="font-heading uppercase font-semibold tracking-tight text-navy text-xl">
                {editing === "new" ? "Nouvel article" : "Modifier l'article"}
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="f-title">Titre</label>
                  <input id="f-title" data-testid="editor-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-category">Catégorie</label>
                  <select id="f-category" data-testid="editor-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                    {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-status">Statut</label>
                  <select id="f-status" data-testid="editor-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={inputCls}>
                    <option value="draft">Brouillon</option>
                    <option value="published">Publié</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-author">Auteur</label>
                  <input id="f-author" data-testid="editor-author" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-date">Date de publication</label>
                  <input id="f-date" data-testid="editor-date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="f-image">Image principale (URL)</label>
                  <input id="f-image" data-testid="editor-image" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder="https://…" className={inputCls} />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="f-video">Vidéo YouTube (URL, optionnel)</label>
                  <input id="f-video" data-testid="editor-video" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.youtube.com/watch?v=…" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-ext-url">Lien externe (optionnel)</label>
                  <input id="f-ext-url" data-testid="editor-external-url" type="url" value={form.external_url || ""} onChange={(e) => setForm({ ...form, external_url: e.target.value })} placeholder="https://…" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="f-ext-label">Libellé du bouton externe</label>
                  <input id="f-ext-label" data-testid="editor-external-label" value={form.external_label || ""} onChange={(e) => setForm({ ...form, external_label: e.target.value })} placeholder="Lire l'article original" className={inputCls} />
                </div>
                <div className="sm:col-span-2 border border-navy/15 bg-mist/40 p-5 space-y-4" data-testid="editor-results-section">
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="editor-show-results-label">
                    <input
                      type="checkbox"
                      checked={form.show_in_results}
                      onChange={(e) => setForm({ ...form, show_in_results: e.target.checked })}
                      data-testid="editor-show-results"
                      className="h-4 w-4 accent-[#D90429]"
                    />
                    <span className="font-heading uppercase tracking-wider text-sm text-navy">Afficher cette course dans les Résultats</span>
                  </label>
                  {form.show_in_results && (
                    <div className="space-y-4 pt-2">
                      <p className="text-xs text-steel leading-relaxed">
                        Saisissez uniquement les résultats réels de la course : aucune place n'est déduite du texte de
                        l'article. La fiche est créée automatiquement sur la page Résultats avec un lien vers cet
                        article, et toute modification est répercutée immédiatement.
                      </p>
                      <div className="grid sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-3 lg:col-span-1">
                          <label className={labelCls} htmlFor="f-course-nom">Nom de la course</label>
                          <input id="f-course-nom" data-testid="editor-course-nom" value={form.resultat_course.nom} onChange={(e) => setCourse({ nom: e.target.value })} className={inputCls} placeholder="GP de …" />
                        </div>
                        <div>
                          <label className={labelCls} htmlFor="f-course-date">Date</label>
                          <input id="f-course-date" data-testid="editor-course-date" type="date" value={form.resultat_course.date} onChange={(e) => setCourse({ date: e.target.value })} className={inputCls} />
                        </div>
                        <div>
                          <label className={labelCls} htmlFor="f-course-categorie">Équipe / catégorie</label>
                          <input id="f-course-categorie" data-testid="editor-course-categorie" value={form.resultat_course.categorie} onChange={(e) => setCourse({ categorie: e.target.value })} className={inputCls} placeholder="N3, Minimes, Féminines…" />
                        </div>
                        <div className="sm:col-span-3 lg:col-span-1">
                          <label className={labelCls} htmlFor="f-course-equipe">Résultat par équipe (optionnel)</label>
                          <input id="f-course-equipe" data-testid="editor-course-equipe" value={form.resultat_course.resultat_equipe} onChange={(e) => setCourse({ resultat_equipe: e.target.value })} className={inputCls} placeholder="3e, 1re…" />
                        </div>
                      </div>
                      <div>
                        <p className={labelCls}>Coureurs du club et places obtenues</p>
                        <div className="space-y-2.5" data-testid="editor-course-riders">
                          {form.resultat_course.coureurs.map((c, i) => (
                            <div key={i} className="flex flex-wrap items-center gap-2.5" data-testid={`editor-rider-${i}`}>
                              <input data-testid={`editor-rider-nom-${i}`} value={c.nom} onChange={(e) => setRider(i, { nom: e.target.value })} placeholder="Nom du coureur" className={`${inputCls} flex-1 min-w-40`} />
                              <input data-testid={`editor-rider-place-${i}`} value={c.place} onChange={(e) => setRider(i, { place: e.target.value })} placeholder="Place (1er, 2e…)" className={`${inputCls} !w-36`} />
                              <button type="button" onClick={() => removeRider(i)} data-testid={`editor-rider-remove-${i}`} className="p-2.5 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer ce coureur">
                                <Trash2 size={15} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button type="button" onClick={addRider} data-testid="editor-rider-add" className="mt-3 inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-4 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                          <Plus size={14} /> Ajouter un coureur
                        </button>
                      </div>
                      {form.resultat_course.coureurs.length === 0 && (
                        <label className="flex items-center gap-3 cursor-pointer" data-testid="editor-course-avenir-label">
                          <input
                            type="checkbox"
                            checked={form.resultat_course.a_venir}
                            onChange={(e) => setCourse({ a_venir: e.target.checked })}
                            data-testid="editor-course-avenir"
                            className="h-4 w-4 accent-[#D90429]"
                          />
                          <span className="text-sm text-navy/80">Afficher la course comme « Résultats à venir » (aucune place renseignée)</span>
                        </label>
                      )}
                    </div>
                  )}
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls} htmlFor="f-content">Contenu</label>
                  <textarea id="f-content" data-testid="editor-content" rows={10} required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className={inputCls} placeholder="Texte de l'article. Séparez les paragraphes par une ligne vide." />
                </div>
              </div>
              <div className="flex gap-3">
                <button type="submit" disabled={saving} data-testid="editor-save" className="inline-flex items-center gap-2 bg-rouge text-white font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:bg-navy disabled:opacity-60 transition-colors duration-300">
                  {saving && <Loader2 size={15} className="animate-spin" />}
                  Enregistrer
                </button>
                <button type="button" onClick={() => setEditing(null)} data-testid="editor-cancel" className="border border-navy/25 text-navy font-heading uppercase tracking-wider text-sm px-7 py-3.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300">
                  Annuler
                </button>
              </div>
            </form>
          )}

          {busy ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
          ) : articles.length === 0 ? (
            <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="admin-empty">
              <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Aucun article</p>
              <p className="mt-2 text-sm text-steel">Publiez la première actualité du club avec le bouton « Nouvel article ».</p>
            </div>
          ) : (
            <div className="bg-white border border-navy/10" data-testid="admin-articles-table">
              {articles.map((a) => (
                <div key={a.id} className="flex flex-wrap items-center gap-4 px-6 py-4 border-b border-navy/10 last:border-0">
                  <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${a.status === "published" ? "bg-green-100 text-green-700" : "bg-steel/15 text-steel"}`}>
                    {a.status === "published" ? "Publié" : "Brouillon"}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-heading uppercase font-semibold text-navy truncate">{a.title}</p>
                    <p className="text-xs text-steel">{a.category} · {fmtDate(a.date)}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {a.status === "published" && (
                      <Link to={`/actualites/${a.slug}`} target="_blank" className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Voir l'article">
                        <ExternalLink size={16} />
                      </Link>
                    )}
                    <button onClick={() => startEdit(a)} data-testid={`edit-${a.slug}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Modifier">
                      <Pencil size={16} />
                    </button>
                    <button onClick={() => remove(a)} data-testid={`delete-${a.slug}`} className="p-2 text-navy/50 hover:text-rouge transition-colors" aria-label="Supprimer">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      )}

      {tab === "peloton" && (
        <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
          <div className="flex items-center justify-between mb-8">
            <p className="text-steel text-sm" data-testid="peloton-count">
              {requests.length} demande{requests.length > 1 ? "s" : ""} / paiement{requests.length > 1 ? "s" : ""}
            </p>
            <button
              onClick={loadPeloton}
              data-testid="peloton-refresh"
              className="inline-flex items-center gap-2 border border-navy/25 text-navy font-heading uppercase tracking-wider text-xs px-5 py-2.5 hover:border-rouge hover:text-rouge transition-[border-color,color] duration-300"
            >
              Actualiser
            </button>
          </div>

          {busyPel ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
          ) : requests.length === 0 ? (
            <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="peloton-empty">
              <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Aucune demande</p>
              <p className="mt-2 text-sm text-steel">Les demandes de contact et les paiements du Peloton des Entreprises apparaîtront ici.</p>
            </div>
          ) : (
            <div className="bg-white border border-navy/10" data-testid="peloton-table">
              {requests.map((r) => (
                <div key={r.id} className="px-6 py-5 border-b border-navy/10 last:border-0" data-testid={`peloton-row-${r.id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${r.type === "paiement" ? "bg-navy text-white" : "bg-mist text-navy border border-navy/15"}`}>
                      {r.type === "paiement" ? "Paiement" : "Contact"}
                    </span>
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${STATUT_STYLES[r.statut] || "bg-steel/15 text-steel"}`}>
                      {r.statut}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading uppercase font-semibold text-navy">
                        {r.prenom} {r.nom}
                        {r.entreprise ? <span className="text-steel font-normal"> — {r.entreprise}</span> : null}
                      </p>
                      <p className="text-xs text-steel">
                        {r.email} · {r.telephone} · {fmtDate(r.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading uppercase font-semibold text-navy text-sm">{r.formule}</p>
                      {r.montant && <p className="text-xs text-rouge font-semibold">{r.montant}</p>}
                    </div>
                    <select
                      value={r.statut}
                      onChange={(e) => changeStatut(r, e.target.value)}
                      data-testid={`peloton-statut-${r.id.slice(0, 8)}`}
                      className="border border-navy/20 bg-white px-3 py-2 text-xs text-navy focus:outline-none focus:border-rouge"
                    >
                      {STATUTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  {r.message && <p className="mt-3 text-sm text-steel border-l-2 border-navy/15 pl-4">{r.message}</p>}
                </div>
              ))}
            </div>
          )}
        </main>
      )}
      {tab === "adhesions" && (
        <main className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <p className="text-steel text-sm" data-testid="adhesions-count">
              {adhesions.length} inscription{adhesions.length > 1 ? "s" : ""}
            </p>
            <button
              onClick={exportAdhesions}
              data-testid="adhesions-export"
              className="inline-flex items-center gap-2 bg-navy text-white font-heading uppercase tracking-wider text-xs px-5 py-2.5 hover:bg-rouge transition-colors duration-300"
            >
              Exporter (CSV)
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-8" data-testid="adhesions-filtres">
            {ADH_FILTRES.map((f) => (
              <button
                key={f.v}
                onClick={() => { setFiltreAdh(f.v); loadAdhesions(f.v); }}
                data-testid={`adh-filtre-${f.v}`}
                className={`font-heading uppercase tracking-wider text-xs px-4 py-2 border transition-[background-color,color,border-color] duration-300 ${
                  filtreAdh === f.v ? "bg-navy border-navy text-white" : "border-navy/20 text-navy/70 hover:border-rouge hover:text-rouge"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {busyAdh ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-rouge" size={28} /></div>
          ) : adhesions.length === 0 ? (
            <div className="border border-dashed border-navy/25 px-8 py-16 text-center" data-testid="adhesions-empty">
              <p className="font-heading uppercase font-semibold tracking-tight text-navy text-2xl">Aucune inscription</p>
              <p className="mt-2 text-sm text-steel">Les adhésions en ligne apparaîtront ici.</p>
            </div>
          ) : (
            <div className="bg-white border border-navy/10" data-testid="adhesions-table">
              {adhesions.map((r) => (
                <div key={r.id} className="px-6 py-5 border-b border-navy/10 last:border-0" data-testid={`adhesion-row-${r.id.slice(0, 8)}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className={`shrink-0 text-[10px] font-bold uppercase tracking-[0.2em] px-2.5 py-1 ${ADH_STATUT_STYLES[r.statut] || "bg-steel/15 text-steel"}`}>
                      {r.statut}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-heading uppercase font-semibold text-navy">{r.prenom} {r.nom}</p>
                      <p className="text-xs text-steel">
                        {r.email} · {r.telephone} · né(e) le {r.date_naissance} · inscrit le {fmtDate(r.created_at)}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-heading uppercase font-semibold text-navy text-sm">{r.licence_label}</p>
                      <p className="text-xs text-rouge font-semibold">{r.total_label}</p>
                    </div>
                    <select
                      value={r.statut}
                      onChange={(e) => changeAdhStatut(r, e.target.value)}
                      data-testid={`adhesion-statut-${r.id.slice(0, 8)}`}
                      className="border border-navy/20 bg-white px-3 py-2 text-xs text-navy focus:outline-none focus:border-rouge"
                    >
                      {ADH_STATUTS.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  {r.equipements?.length > 0 && (
                    <p className="mt-3 text-sm text-steel border-l-2 border-navy/15 pl-4">
                      Équipements : {r.equipements.map((e) => `${e.label} ${e.taille} ×${e.quantite}`).join(" · ")}
                    </p>
                  )}
                  <p className="mt-2 text-xs text-steel">
                    {r.adresse}, {r.code_postal} {r.ville}
                    {r.paid_at ? ` · payé le ${fmtDate(r.paid_at)}` : ""}
                  </p>
                </div>
              ))}
            </div>
          )}
        </main>
      )}
    </div>
  );
}
